"use client";

import { useEffect, useState } from "react";
import Button from "../ui/Button";
import Image from "next/image";
import Link from "next/link";
import { useCartOptional } from "../contexts/CartContext";
import DankLifestyleMerchProductTile from "./DankLifestyleMerchProductTile";
import { getStoredStoreId } from "@/lib/store-storage";
import { extractVariants } from "@/lib/variant-extractor";
import { PRODUCT_PLACEHOLDER_IMAGE } from "@shared/lib/product-placeholder";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";

export type VariantOption = {
  key: string;
  label: string;
  price: number;
  inStock: number;
  productSlug: string | null;
  actualPackageId: string | null;
  heroShotUri: string | null;
  productId: string;
  color: string | null;
  size: string | null;
  sizeOrWeight: string | null;
};

export type MerchTileProduct = {
  id: string;
  title: string;
  href: string;
  baseImage: string;
  basePrice: number;
  options: VariantOption[];
  colors: string[];
  sizes: string[];
  classificationName?: string | null;
};

export function ensureAbsoluteImageUrl(uri: string | null | undefined) {
  if (!uri) return null;
  if (uri.startsWith("http://") || uri.startsWith("https://")) return uri;
  const clean = uri.startsWith("/") ? uri : `/${uri}`;
  return `${API_BASE}${clean}`;
}

// Helper functions and extractVariants are now imported from @/lib/variant-extractor

export function transformProduct(product: any): MerchTileProduct | null {
  if (!product) return null;

  // Prefer backend grouped display name
  const title = product?.name || product?.masterProductName || product?.classificationName || "Product";
  const classificationName = product?.classificationName || null;

  const groupProducts = Array.isArray(product?.groupProducts) ? product.groupProducts : [];

  // Link: use any available slug
  const slug = product?.slug || groupProducts?.[0]?.slug || "";
  // Product detail route is /product/[productId], not /products/. The plural
  // form 404'd every merch tile.
  const href = slug ? `/product/${slug}` : "#";

  // Base image: prefer product, otherwise first variant
  const baseHero = product?.heroShotUri || groupProducts?.[0]?.heroShotUri || null;
  const baseImage = ensureAbsoluteImageUrl(baseHero) || PRODUCT_PLACEHOLDER_IMAGE;

  // Build options from groupProducts (admin group-view style with variant extraction)
  const optionsWithVariants: VariantOption[] = groupProducts
    .map((gp: any) => {
      const gpId = String(gp?.id || gp?.productId || "");
      if (!gpId) return null;

      const gpAvail =
        Array.isArray(gp?.availabilities) && gp.availabilities.length > 0 ? gp.availabilities[0] : null;
      if (!gpAvail) return null;

      const inStock = Number(gpAvail.inStock || 0);
      if (inStock <= 0) return null;

      const price =
        gpAvail.price !== null && gpAvail.price !== undefined
          ? Number(gpAvail.price)
          : Number(gpAvail.unitCost || 0);

      const sizeOrWeight = gp?.sizeOrWeight ? String(gp.sizeOrWeight) : null;
      const gpName = gp?.name || gp?.product?.name || "";
      const gpBrand = gp?.brandName || gp?.product?.brandName || product?.brandName || null;
      const gpClassification = gp?.classificationName || gp?.product?.classificationName || classificationName;

      // Extract color and size using admin panel logic
      const variants = extractVariants(gpName, gpClassification, gpBrand, sizeOrWeight);

      // Build label: "Color Size" or "Size" or "Default"
      let label = "Default";
      if (variants.color && variants.size) {
        label = `${variants.color} ${variants.size}`;
      } else if (variants.color) {
        label = variants.color;
      } else if (variants.size) {
        label = variants.size;
      } else if (sizeOrWeight) {
        label = sizeOrWeight;
      }

      return {
        key: `${product.id}:${gpId}:${gpAvail.packageId || ""}`,
        label,
        price: Number.isFinite(price) ? price : 0,
        inStock,
        productSlug: (gp?.slug || null) as string | null,
        actualPackageId: (gpAvail?.packageId || null) as string | null,
        heroShotUri: (gp?.heroShotUri || null) as string | null,
        productId: gpId,
        color: variants.color,
        size: variants.size,
        sizeOrWeight,
      } satisfies VariantOption;
    })
    .filter(Boolean) as VariantOption[];

  // Extract unique colors and sizes
  const colorsSet = new Set<string>();
  const sizesSet = new Set<string>();
  
  optionsWithVariants.forEach(opt => {
    if (opt.color) colorsSet.add(opt.color);
    if (opt.size) sizesSet.add(opt.size);
  });

  const colors = Array.from(colorsSet).sort();
  const sizes = Array.from(sizesSet).sort((a, b) => {
    // Sort sizes: numeric first, then alphabetical
    const aNum = parseFloat(a.replace(/[^0-9.]/g, '')) || 0;
    const bNum = parseFloat(b.replace(/[^0-9.]/g, '')) || 0;
    if (aNum !== 0 || bNum !== 0) {
      return aNum - bNum;
    }
    // Size order: XS, S, M, L, XL, XXL, etc.
    const sizeOrder = ['xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '2xl', '3xl', '4xl', 'small', 'medium', 'large', 'extra small', 'extra large', 'one size'];
    const aIndex = sizeOrder.findIndex(size => a.toLowerCase().includes(size));
    const bIndex = sizeOrder.findIndex(size => b.toLowerCase().includes(size));
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  // Base price = first option price if available
  const basePrice = optionsWithVariants.length > 0 ? optionsWithVariants[0].price : 0;

  return {
    id: String(product.id),
    title,
    href,
    baseImage,
    basePrice,
    options: optionsWithVariants,
    colors,
    sizes,
    classificationName,
  };
}

type DankLifestyleSectionProps = {
  apiPath?: string;
  /** When set (e.g. location detail page), merch inventory uses this store instead of cart / cookie. */
  covaLocationId?: number | null;
};

export default function DankLifestyleSection({
  apiPath = "/api/homepage/merch",
  covaLocationId: covaLocationIdProp = null,
}: DankLifestyleSectionProps = {}) {
  const cart = useCartOptional();
  const state = cart?.state;
  const propId = covaLocationIdProp != null ? Number(covaLocationIdProp) : NaN;
  const storeId =
    Number.isFinite(propId) && propId > 0
      ? propId
      : state?.storeCovaLocationId || getStoredStoreId();
  const [products, setProducts] = useState<MerchTileProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchMerch = async () => {
      try {
        const params = new URLSearchParams();
        if (storeId) params.set("covaLocationId", String(storeId));
        const res = await fetch(`${API_BASE}${apiPath}${params.toString() ? `?${params.toString()}` : ""}`, {
          cache: "no-store",
        });
        if (!res.ok) {
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (data.success && Array.isArray(data.products)) {
          const transformed = data.products.map(transformProduct).filter(Boolean) as MerchTileProduct[];
          setProducts(transformed);

          if (data.heroImageUrl) {
            setHeroImageUrl(data.heroImageUrl);
          } else {
            setHeroImageUrl(null);
          }
        }
      } catch (error) {
        console.error("[DankLifestyleSection] Failed to fetch merch:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMerch();
  }, [apiPath, storeId]);

  // Don't render if no products
  if (loading || products.length === 0) {
    return null;
  }

  return (
    <section className="w-full px-4 sm:px-6 lg:px-8">
      <div className="w-full">
        {/* Top Section - Hero */}
        <div className="flex flex-col lg:flex-row items-center sm:gap-8 gap-4 sm:mb-16 mb-8 max-sm:flex-col-reverse">
          {/* Left Column - Text Content */}
          <div className="w-full lg:w-[40%] flex flex-col gap-8">
            <div className="flex flex-col gap-3">
              <h2 className="text-h2-mobile sm:text-h2-desktop font-bold text-text-primary leading-tight">
                Wear the Dank lifestyle
              </h2>

              <p className="text-base text-text-secondary leading-relaxed">
                It&apos;s not just about what you smoke. It&apos;s about how you rep the culture. Our apparel and
                accessories are made for comfort, style, and Calgary cannabis fans. Hitting the Stampede or just
                hanging at home, wear the brand that gets you.
              </p>
            </div>

            <Button asChild variant="primary" size="lg" className="w-fit">
          <Link href="/collections/apparel">
              <span className="text-base font-medium text-constant-white">
                Explore the collection
              </span>
            </Link>
        </Button>
          </div>

          {/* Right Column - Hero Image */}
          <div className="w-full lg:w-[60%] flex items-start justify-end">
            <div className="w-full relative rounded-2xl overflow-hidden sm:h-[540px] h-[228px] flex items-center justify-center">
              {/* Background Shape */}
              <div className="absolute inset-0 z-0">
                <Image
                  src="/media/sections/lifestyle/backgrounds/background-shape.webp"
                  alt=""
                  fill
                  className="object-cover"
                  aria-hidden="true"
                />
              </div>
              
              {/* Hero Image */}
              <div className="relative z-10 sm:w-[72%] w-[90%] aspect-[9/5] border-constant-white rounded-2xl drop-shadow sm:border-[10px] border-[4px] -rotate-[3deg]">
                {heroImageUrl ? (
                  <img
                    src={heroImageUrl}
                    alt="Dank lifestyle - Cannabis enthusiasts wearing Dank apparel"
                    className="object-cover w-full h-full bg-black/50 rounded-xl"
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                  />
                ) : (
                  <Image
                    src="/media/sections/lifestyle/merch/dank-merch.webp"
                    alt="Dank lifestyle - Cannabis enthusiasts wearing Dank apparel"
                    fill
                    className="object-cover w-full h-full bg-black/50 rounded-xl"
                    sizes="(max-width: 1024px) 100vw, 60vw"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Product Grid */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {products.map((product) => (
            <DankLifestyleMerchProductTile key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
